// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import { ReactElement, useContext, useMemo } from 'react';
import client from '@Client';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import React from 'react';
import { handleNotificationError, openMessage } from '@Helpers/';
import AttackImport from '@Organisms/AttackPatterns/AttackImport';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { Link } from 'react-router-dom';
import { RouteType } from '@Types';
import useConfigList from '@Hooks/useConfigList';
import { THList } from '@Molecules/THList';
import { useCurrentUser } from '@Queries/userQueries';
import usePermissions from '@Hooks/usePermissions';
import { AdminPermissionsEnum, ObjectTypeV1 as ObjectType, CatalogOutput } from 'thehive-sdk';
import { StatusContext } from '@Context/StatusProvider';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatDate } from '@Molecules/Format';
import { computeEventList } from '@Organisms/CatalogPattern/eventList';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ICONS } from '@Constants/iconsApp';
import { ImportButton } from '@Molecules/FileImportDrawer/FileImportDrawer';
import { useDrawer } from '@Hooks/useDrawer';
import usePageTitle from '@Hooks/usePageTitle';

import './AttackPatterns.scss';

interface CatalogListNameProps {
    id: string;
    name: string;
}

const CatalogListName = ({ name, id }: CatalogListNameProps): ReactElement => {
    return (
        <Link to={RouteBuilder.compute('administration.catalogPatterns', { catalogId: id })}>
            <span className="catalogList-name__title-label">{name}</span>
        </Link>
    );
};

const CatalogPatternsList = (): ReactElement => {
    const i = useLocalMessage();

    const { visible, setVisible } = useDrawer({});

    const { version } = useContext(StatusContext);

    const config = useConfigList<CatalogOutput>(
        useMemo(
            () => ({
                selector: {
                    head: [{ name: 'listCatalog' }],
                    name: 'get-list-catalog',
                    sort: ['+name'],
                },
                controller: client.CatalogPatternController,
                storageId: StorageFilterEnum.CATALOG_ATTACK_PATTERNS,
                objectType: ObjectType.CATALOG_ATTACK_PATTERNS,
            }),
            [],
        ),
    );

    const { listRefetch, dataSource = [], columnsRenderer, countRefetch, status = 'loading' } = config;

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_PATTERN],
        permissionsList: permissions,
    });

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.CatalogPatternController,
                locale: i,
                onSuccess: (count, message) => {
                    listRefetch();
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                },
                onError: handleNotificationError,
            }),
        [i, listRefetch, countRefetch],
    );

    const columns = columnsRenderer([
        {
            title: [{ label: i('catalog_pattern.list.table.columns.name'), sortField: 'name' }],
            className: 'catalog-list__name column_preview',
            render: ({ name, _id }): ReactElement => <CatalogListName name={name} id={_id} />,
        },
        {
            title: [{ label: i('catalog_pattern.list.table.columns.created-by'), sortField: '_createdBy' }],
            className: 'catalog-list__assignee',
            render: ({ createdBy }): ReactElement => <AssigneeInfo assignee={createdBy} />,
        },
        {
            title: [{ label: i('catalog_pattern.list.table.columns.created-at'), sortField: '_createdAt' }],
            className: 'catalog-list__date',
            render: ({ createdAt }): ReactElement => <FormatDate date={createdAt} />,
        },
        {
            className: 'catalog-list__column-actions',
            render: ({ _id }): ReactElement | null =>
                hasPermissions ? (
                    <ActionsList
                        actionList={{
                            delete: {
                                label: i('general.delete'),
                                confirm: true,
                                message: i('catalog_pattern.list.delete-confirm'),
                                query: () => eventList.onDelete(_id),
                                icon: <ICONS.DeleteIcon />,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const dataGroup = useMemo(
        () => (dataSource ? dataSource?.map(({ name }) => ({ value: name, label: name })) : []),
        [dataSource],
    );

    usePageTitle(i('page-title.admin.entities.attack-patterns'));

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <ImportButton onClick={() => setVisible(true)} label={i('attackPattern.import.label')} />
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    className="attack-pattern__wrapper"
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={<DisplayEmptyData spin={status === 'loading'} label={i('attackPattern.none')} />}
                />
            </THList>

            {visible && (
                <AttackImport
                    dataGroup={dataGroup}
                    onSubmit={(formData): Promise<void> =>
                        client.AttackPatternsController.import(formData)
                            .then(() => {
                                listRefetch();
                            })
                            .catch(handleNotificationError)
                    }
                    visible={visible}
                    onVisibleChange={setVisible}
                    thVersion={version}
                />
            )}
        </>
    );
};
const path = RouteBuilder.compute('administration.attackPatterns');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.attackPatterns',
        icon: <ICONS.TTPIcon />,
    },
    content: <CatalogPatternsList />,
    breadcrumb: {
        label: 'breadcrumb.global.attack-patterns',
    },
    Context: ListProvider,
    permissionNeeded: [AdminPermissionsEnum.MANAGE_PATTERN],
    space: ['admin'],
    exact: true,
};
